import { lazy, Suspense, type ReactElement } from 'react';
import {
  type LoaderFunctionArgs,
  createBrowserRouter,
  createRoutesFromElements,
  Route
} from 'react-router';
import {
  RootErrorBoundary,
  Loader,
  RedirectWithQuery
} from '@link/share/components';
import { LOCALE_LIST, DEFAULT_LOCALE } from '@link/share/i18n';
import i18n from '@link/skytree/i18n';
import { SkytreeLayout } from '@link/skytree/layouts';
import PrivateRoute from './PrivateRoute';
import { sttLangLoader } from './sttLang';
import styles from './loader.module.css';

const Home = lazy(() => import('@link/skytree/views/Home'));
const Product = lazy(() => import('@link/skytree/views/Product'));
const SkytreeProduct = lazy(
  () => import('@link/skytree/views/skytree/Product')
);
const SkytreeBookConfirm = lazy(
  () => import('@link/skytree/views/skytree/BookConfirm')
);
const BookConfirm = lazy(() => import('@link/skytree/views/Book/BookConfirm'));
const BookResult = lazy(() => import('@link/skytree/views/Book/BookResult'));
const Booking = lazy(() => import('@link/skytree/views/Booking'));
const BookingDetail = lazy(
  () => import('@link/skytree/views/Booking/BookingDetail')
);
const Invoice = lazy(() => import('@link/skytree/views/Invoice'));
const FAQ = lazy(() => import('@link/skytree/views/FAQ'));
const Terms = lazy(() => import('@link/share/views/Terms'));
const Privacy = lazy(() => import('@link/share/views/Privacy'));
const Law = lazy(() => import('@link/skytree/views/Law'));
const NotFound = lazy(() => import('@link/skytree/views/NotFound'));
const TooManyRequests = lazy(
  () => import('@link/skytree/views/TooManyRequests')
);
const Login = lazy(() => import('@link/skytree/views/Login'));
const Profile = lazy(() => import('@link/skytree/views/Profile'));
const ChangePassword = lazy(() => import('@link/skytree/views/ChangePassword'));
const ResetPassword = lazy(() => import('@link/skytree/views/ResetPassword'));
const EmailVerification = lazy(
  () => import('@link/skytree/views/EmailVerification')
);

const lazyView = (view: ReactElement) => {
  return (
    <Suspense fallback={<Loader className={styles.loader} />}>{view}</Suspense>
  );
};

const langLoader = async ({
  params,
  request
}: LoaderFunctionArgs): Promise<{ lang: string }> => {
  sttLangLoader({ request });
  if (params.lang && LOCALE_LIST.includes(params.lang)) {
    return { lang: params.lang };
  }
  return { lang: DEFAULT_LOCALE };
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route errorElement={<RootErrorBoundary />}>
      <Route path="/:lang" element={<SkytreeLayout />} loader={langLoader}>
        <Route index element={<RedirectWithQuery to="home" />} />
        <Route path="home" element={lazyView(<Home />)} />
        <Route path="product/:productId" element={lazyView(<Product />)} />
        <Route
          path="skytree/product/:productId"
          element={lazyView(<SkytreeProduct />)}
        />
        <Route element={<PrivateRoute />}>
          <Route
            path="skytree/book/confirm"
            element={lazyView(<SkytreeBookConfirm />)}
            handle={{ disableLang: true }}
          />
          <Route
            path="book/confirm"
            element={lazyView(<BookConfirm />)}
            handle={{ disableLang: true }}
          />
          <Route
            path="book/result/:bookingId"
            element={lazyView(<BookResult />)}
            handle={{ disableLang: true }}
          />
          <Route
            path="booking"
            element={lazyView(<Booking />)}
            handle={{ title: i18n.t('views.booking.title') }}
          />
          <Route
            path="booking/:bookingId"
            element={lazyView(<BookingDetail />)}
            handle={{ title: i18n.t('views.booking.title') }}
          />
          <Route path="invoice/:bookingId" element={lazyView(<Invoice />)} />
          <Route
            path="profile"
            element={lazyView(<Profile />)}
            handle={{ title: i18n.t('share.profile.title') }}
          />
          <Route
            path="change-password"
            element={lazyView(<ChangePassword />)}
            handle={{ title: i18n.t('share.password.changePassword') }}
          />
        </Route>
        <Route path="faq" element={lazyView(<FAQ />)} />
        <Route
          path="terms"
          element={lazyView(<Terms />)}
          handle={{ title: i18n.t('views.terms.title') }}
        />
        <Route
          path="privacy"
          element={lazyView(<Privacy />)}
          handle={{ title: i18n.t('views.privacy.title') }}
        />
        <Route
          path="law"
          element={lazyView(<Law />)}
          handle={{ title: i18n.t('law.title') }}
        />
        <Route
          path="login"
          element={lazyView(<Login />)}
          handle={{ title: i18n.t('share.login.title') }}
        />
        <Route
          path="email-verification"
          element={lazyView(<EmailVerification />)}
          handle={{
            title: i18n.t('share.emailVerification.title')
          }}
        />
        <Route
          path="reset-password"
          element={lazyView(<ResetPassword />)}
          handle={{ title: i18n.t('share.password.resetPassword') }}
        />
        <Route
          path="too-many-requests"
          element={lazyView(<TooManyRequests />)}
        />
        <Route path="*" element={lazyView(<NotFound />)} />
      </Route>
      <Route path="/" element={<RedirectWithQuery />} />
    </Route>
  )
);

export default router;
